// src/hooks/rq/usePets.js

import { useInfiniteQuery } from '@tanstack/react-query';
import API from '../../api/client';

/**
 * Hook para obtener el listado paginado de mascotas con React Query.
 * Usa useInfiniteQuery para fetch automático de páginas según filtros,
 * ubicación y estado de autenticación.
 *
 * @param {{ species?: string, sex?: string, filtersLocation?: {lat: number, lng: number, radius?: number}, coords: {lat: number, lng: number}, isAuthenticated: boolean }} options
 * @returns {{
 *   data: any,                   // Objeto con pages: Array<{ results: Array, next: string|null }>
 *   isLoading: boolean,
 *   isError: boolean,
 *   error: any,
 *   fetchNextPage: Function,     // Función para cargar la siguiente página
 *   hasNextPage: boolean,        // true si hay más páginas según la respuesta
 *   isFetchingNextPage: boolean, // true mientras carga la siguiente página
 *   refetch: Function            // Forzar refetch de todas las páginas
 * }}
 */
export function usePets({ species, sex, filtersLocation, coords, isAuthenticated }) {
  // Determinamos lat/lng y radius para los params y la cache key
  const lat = filtersLocation?.lat ?? coords.lat;
  const lng = filtersLocation?.lng ?? coords.lng;
  const radiusParam = filtersLocation?.radius;
  const radiusKey = radiusParam != null ? radiusParam : 'none';

  // Definimos la clave de query aquí para poder usarla en los logs
  const queryKey = [
    'pets',
    species || 'all',
    sex       || 'all',
    lat,
    lng,
    radiusKey,
    isAuthenticated ? 'auth' : 'noauth'
  ];

  return useInfiniteQuery({
    queryKey,

    // queryFn recibe pageParam y hace GET /pets/?page=pageParam&...
    queryFn: async ({ pageParam = 1 }) => {
      console.log(
        '[usePets] ▶ queryFn arrancado con pageParam=',
        pageParam,
        'key=',
        queryKey
      );

      const params = {
        page: pageParam,
        _: Date.now(), // bust cache
        ...(species && { species }),
        ...(sex && { sex }),
        lat,
        lng,
        ...(radiusParam != null && { radius: radiusParam })
      };

      const token = localStorage.getItem('accessToken');
      const config = {
        params,
        ...(token && { headers: { Authorization: `Bearer ${token}` } })
      };

      const response = await API.get('/pets/', config);
      return response.data; // { results: [...], next: string|null, ... }
    },

    // Dado el último page, indicamos cómo calcular el siguiente:
    getNextPageParam: (lastPage, pages) =>
      lastPage.next ? pages.length + 1 : undefined,

    // Logs para entender el ciclo de vida de la query
    onSuccess: data => {
      console.log('[usePets] ✅ onSuccess páginas=', data.pages.length);
    },
    onError: error => {
      console.error('[usePets] ❌ onError', error);
    },
    onSettled: (data, error) => {
      console.log(
        '[usePets] 🔄 onSettled — fue refetched tras invalidate?',
        { data, error }
      );
    },

    // No recargar automáticamente al montar de nuevo
    refetchOnMount: false,
    // No disparar refetch al enfocar ventana
    refetchOnWindowFocus: false

    // staleTime, cacheTime y retry vienen del QueryClient global
  });
}
